import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule, DatePipe, formatDate } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatRadioModule } from '@angular/material/radio';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';

import { CorrespondenceService } from '../../../domain/services/correspondence/correspondence.service';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { DialogService } from '../../../domain/services/dialog/dialog.service';
import {
  ACCOUNT_STATUSES, ACCOUNT_TYPES, AccountPick, CorrespondenceAudience, CorrespondenceAudienceType,
  CorrespondenceTemplate
} from '../../../domain/models/correspondence.model';
import { CorrespondenceFilesComponent } from '../shared/correspondence-files.component';
import { HtmlEditorComponent } from '../../../common/html-editor/html-editor.component';
import { EmailPreviewComponent, previewShown, setPreviewShown } from '../../../common/html-editor/email-preview.component';
import { InsertVariableComponent } from '../../../common/html-editor/insert-variable.component';

/**
 * Compose a broadcast: pick a template (optional) → edit subject/body/files → choose the
 * audience (everyone / specific accounts / filtered) → send now or schedule.
 */
@Component({
  selector: 'app-compose-correspondence',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule, RouterModule, DatePipe,
    MatStepperModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule,
    MatDatepickerModule, MatNativeDateModule, MatIconModule, MatProgressSpinnerModule, MatCardModule,
    MatDividerModule, MatRadioModule, MatChipsModule, MatAutocompleteModule, CorrespondenceFilesComponent, HtmlEditorComponent, EmailPreviewComponent, InsertVariableComponent,
  ],
  templateUrl: './compose-correspondence.component.html',
  styleUrl: './compose-correspondence.component.css'
})
export class ComposeCorrespondenceComponent implements OnInit {
  showPreview = previewShown();
  togglePreview(): void { this.showPreview = !this.showPreview; setPreviewShown(this.showPreview); }

  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('editor') editor?: HtmlEditorComponent;
  @ViewChild(CorrespondenceFilesComponent) files?: CorrespondenceFilesComponent;

  readonly accountTypes = ACCOUNT_TYPES;
  readonly accountStatuses = ACCOUNT_STATUSES;
  readonly states = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN',
    'MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA',
    'WA','WV','WI','WY'
  ];

  tokens: string[] = [];
  templates: CorrespondenceTemplate[] = [];
  filteredTemplates: CorrespondenceTemplate[] = [];
  templateSearchQuery = '';
  selectedTemplate: CorrespondenceTemplate | null = null;

  messageForm: FormGroup;
  audienceForm: FormGroup;
  scheduleForm: FormGroup;

  pickedAccounts: AccountPick[] = [];
  accountQuery = '';
  accountOptions: AccountPick[] = [];
  private accountSearch$ = new Subject<string>();

  audienceCount: number | null = null;
  audienceCountLoading = false;
  sendLoading = false;
  sendComplete = false;
  sentStatus = '';

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: CorrespondenceService,
    private readonly breadcrumbs: BreadcrumbService,
    private readonly dialog: DialogService,
    private readonly router: Router,
  ) {
    this.messageForm = this.fb.group({
      subject: ['', [Validators.required, Validators.maxLength(250)]],
      body: ['', Validators.required],
    });
    this.audienceForm = this.fb.group({
      type: ['ALL' as CorrespondenceAudienceType, Validators.required],
      accountType: [null],
      status: [null],
      state: [''],
      city: [''],
      zip: [''],
      registeredFrom: [null],
      registeredTo: [null],
    });
    this.scheduleForm = this.fb.group({
      scheduleType: ['NOW', Validators.required],
      scheduledDate: [null],
      scheduledTime: ['09:00'],
    });
  }

  ngOnInit(): void {
    this.breadcrumbs.updateBreadcrumbs([
      { icon: 'mail', url: '/app/correspondence/emails', label: 'Correspondence' },
      { icon: '', url: '/app/correspondence/compose', label: 'Compose message' }
    ]);
    this.service.getTokens().subscribe((t) => (this.tokens = t));
    this.service.getTemplates().subscribe((templates) => {
      this.templates = templates.filter((t) => t.active);
      this.applyTemplateFilter();
    });
    this.scheduleForm.get('scheduleType')?.valueChanges.subscribe((v) => {
      const c = this.scheduleForm.get('scheduledDate');
      v === 'LATER' ? c?.setValidators(Validators.required) : c?.clearValidators();
      c?.updateValueAndValidity();
    });
    this.accountSearch$.pipe(
      debounceTime(250), distinctUntilChanged(),
      switchMap((q) => q.trim().length < 2 ? of([]) : this.service.searchAccounts(q).pipe(catchError(() => of([]))))
    ).subscribe((list) => (this.accountOptions = list.filter((a) => !this.pickedAccounts.some((p) => p.id === a.id))));
    // Show who will receive the message while staff choose, not only at the last step.
    this.audienceForm.valueChanges.pipe(debounceTime(400)).subscribe(() => this.loadAudienceCount());
    this.loadAudienceCount();
  }

  // ---- step 1: message
  applyTemplateFilter(): void {
    const q = this.templateSearchQuery.toLowerCase();
    this.filteredTemplates = this.templates.filter((t) => !q || t.name.toLowerCase().includes(q) || t.subject.toLowerCase().includes(q));
  }

  /** Cards shown: the most recently edited templates, plus the selected one if it came from search. */
  readonly maxCards = 5;
  get shownTemplates(): CorrespondenceTemplate[] {
    const recent = [...this.templates]
      .sort((a, b) => (b.lastModified ?? '').localeCompare(a.lastModified ?? ''))
      .slice(0, this.maxCards);
    const sel = this.selectedTemplate;
    if (sel && !recent.some((t) => t.id === sel.id)) recent[recent.length - 1] = this.templates.find((t) => t.id === sel.id) ?? sel;
    return recent;
  }

  selectTemplate(t: CorrespondenceTemplate): void {
    if (this.selectedTemplate?.id === t.id) { this.clearTemplate(); return; }
    this.service.getTemplate(t.id).subscribe((full) => {
      this.selectedTemplate = full;
      this.files?.reset();
      this.messageForm.patchValue({ subject: full.subject, body: full.body });
    });
  }

  clearTemplate(): void {
    this.selectedTemplate = null;
    this.files?.reset();
  }

  startBlank(): void {
    this.clearTemplate();
    this.messageForm.reset({ subject: '', body: '' });
  }

  insertToken(token: string): void { this.insert(token); }
  insertImage(cid: string): void { this.insert(`<img src="cid:${cid}" alt="" style="max-width:100%">`); }
  private insert(html: string): void { this.editor?.insert(html); }

  // ---- step 2: audience
  get audienceType(): CorrespondenceAudienceType { return this.audienceForm.value.type; }

  onAccountQuery(q: string): void { this.accountQuery = q; this.accountSearch$.next(q); }

  pickAccount(a: AccountPick): void {
    if (!this.pickedAccounts.some((p) => p.id === a.id)) this.pickedAccounts = [...this.pickedAccounts, a];
    this.accountQuery = '';
    this.accountOptions = [];
    this.loadAudienceCount();
  }

  unpickAccount(a: AccountPick): void {
    this.pickedAccounts = this.pickedAccounts.filter((p) => p.id !== a.id);
    this.loadAudienceCount();
  }

  get audienceValid(): boolean {
    return this.audienceType !== 'ACCOUNTS' || this.pickedAccounts.length > 0;
  }

  audience(): CorrespondenceAudience {
    const v = this.audienceForm.value;
    const day = (d: Date | null) => (d ? formatDate(d, 'yyyy-MM-dd', 'en-US') : null);
    switch (this.audienceType) {
      case 'ACCOUNTS': return { type: 'ACCOUNTS', accountIds: this.pickedAccounts.map((a) => a.id) };
      case 'FILTER': return {
        type: 'FILTER', accountType: v.accountType ?? null, status: v.status ?? null, state: v.state || null,
        city: v.city || null, zip: v.zip || null, registeredFrom: day(v.registeredFrom), registeredTo: day(v.registeredTo),
      };
      default: return { type: 'ALL' };
    }
  }

  setAudience(type: CorrespondenceAudienceType): void { this.audienceForm.patchValue({ type }); }

  /** Plain-language description of the chosen audience (review step). */
  get audienceDescription(): string {
    const v = this.audienceForm.value;
    if (this.audienceType === 'ALL') return 'Every account that is not closed';
    if (this.audienceType === 'ACCOUNTS') return this.pickedAccounts.map((a) => a.name).join(', ') || 'No accounts picked yet';
    const parts = [
      ACCOUNT_STATUSES.find((s) => s.value === v.status)?.label,
      ACCOUNT_TYPES.find((t) => t.value === v.accountType)?.label,
      v.city && `in ${v.city}`, v.state && `(${v.state})`, v.zip && `zip ${v.zip}`,
      v.registeredFrom && `registered since ${formatDate(v.registeredFrom, 'MM/dd/yyyy', 'en-US')}`,
    ].filter(Boolean);
    return parts.length ? `Accounts: ${parts.join(' ')}` : 'All accounts that are not closed';
  }

  loadAudienceCount(): void {
    if (!this.audienceValid) { this.audienceCount = 0; return; }
    this.audienceCountLoading = true;
    this.service.countAudience(this.audience()).subscribe({
      next: (r) => { this.audienceCount = r.count; this.audienceCountLoading = false; },
      error: () => { this.audienceCount = 0; this.audienceCountLoading = false; },
    });
  }

  onAudienceNext(): void { this.loadAudienceCount(); }

  // ---- step 3: schedule & send
  get scheduleType(): string { return this.scheduleForm.value.scheduleType; }

  setSchedule(scheduleType: 'NOW' | 'LATER'): void { this.scheduleForm.patchValue({ scheduleType }); }

  get scheduledAt(): Date | null {
    const d: Date | null = this.scheduleForm.value.scheduledDate;
    if (!d) return null;
    const [h, m] = String(this.scheduleForm.value.scheduledTime || '09:00').split(':').map(Number);
    const out = new Date(d);
    out.setHours(h || 0, m || 0, 0, 0);
    return out;
  }

  onSend(): void {
    if (this.messageForm.invalid || this.scheduleForm.invalid || !this.audienceValid) return;
    this.sendLoading = true;
    const v = this.messageForm.value;
    this.service.send({
      templateId: this.selectedTemplate?.id ?? null,
      subject: v.subject,
      body: v.body,
      audience: this.audience(),
      scheduleAt: this.scheduleType === 'LATER' && this.scheduledAt ? this.scheduledAt.toISOString() : null,
    }, this.files?.value ?? { keepIds: [], attachments: [], inline: [] }).subscribe({
      next: (batch) => {
        this.sendLoading = false;
        this.sendComplete = true;
        this.sentStatus = batch.status;
        this.audienceCount = batch.recipientCount;
        this.stepper.next();
      },
      error: (e) => {
        this.sendLoading = false;
        this.dialog.openErrorDialog('Could not send', e?.error?.message || 'The message was not sent.');
      },
    });
  }

  goBack(): void { this.router.navigate(['/app/correspondence/emails']); }

  startOver(): void {
    this.messageForm.reset({ subject: '', body: '' });
    this.audienceForm.reset({ type: 'ALL', state: '', city: '', zip: '' });
    this.scheduleForm.reset({ scheduleType: 'NOW', scheduledTime: '09:00' });
    this.selectedTemplate = null;
    this.pickedAccounts = [];
    this.files?.reset();
    this.audienceCount = null;
    this.sendComplete = false;
    this.stepper.reset();
  }
}
